<template>
	<div class="page-wrapper">
		<div class="main">

			<!-- LEFT -->
			<div class="data">
				<div class="data__subtitle">K&M REMODELING SERVICES LLC &nbsp;/&nbsp; NC AND SC</div>

				<div class="data__title">
					<h2>Your home.<br><span class="data__title--text">Rebuilt right.</span><span>No surprises.</span></h2>
				</div>

				<div class="data__text">
					<p>Thoughtfully planned, expertly built. One fixed quote, one dedicated team, and one point of contact from demo day to final walkthrough.</p>
				</div>

				<ul>
					<li>LOCAL &amp; FAMILY OWNED - CHARLOTTE AREA</li>
					<li>FIXED PRICE - NO HIDDEN FEES</li>
					<li>COMPLIMENTARY DESIGN CONSULTATION INCLUDED</li>
					<li>FULLY LICENSED &amp; INSURED (NC/SC)</li>
				</ul>

				<div class="data__contacts">
					<div class="data__contact-item">
						<span class="data__contact-label">Phone</span>
						<a href="tel:+19808905255">(980) 890-5255</a>
					</div>
					<div class="data__contact-item">
						<span class="data__contact-label">Email</span>
						<a href="mailto:sales@kmremodelingservices.com">sales@kmremodelingservices.com</a>
					</div>
					<div class="data__contact-item">
						<span class="data__contact-label">Location</span>
						<span class="data__contact-value">Charlotte, NC</span>
					</div>
				</div>
			</div>

			<!-- RIGHT: form -->
			<div class="forms">
				<form id="form-section" ref="form" class="form" novalidate @submit.prevent="handleSubmit">
					<h3>Request your complimentary estimate</h3>
					<p class="form__lead">4 quick questions — takes under a minute</p>

					<!-- Прогресс -->
					<ol class="progress" aria-label="Form progress">
						<li
							v-for="(label, i) in stepLabels"
							:key="label"
							class="progress__item"
							:class="{ 'progress__item--active': i + 1 <= step }"
							:aria-current="i + 1 === step ? 'step' : null"
						>
							<span class="progress__bar"></span>
							<span class="progress__label">{{ i + 1 }} · {{ label }}</span>
						</li>
					</ol>

					<!-- Honeypot: всегда в DOM, вне шагов -->
					<input ref="company" type="text" name="company" style="display:none" tabindex="-1" autocomplete="off">

					<transition name="step-fade" mode="out-in" @after-enter="onStepEnter">
						<!-- STEP 1 -->
						<div v-if="step === 1" key="step-1" class="step">
							<h4 ref="stepTitle" class="step__title" tabindex="-1">What are you planning?</h4>
							<div class="options">
								<OptionCard
									v-for="opt in projectOptions"
									:key="opt.value"
									:title="opt.value"
									:subtitle="opt.hint"
									:icon="opt.icon"
									:selected="form.project === opt.value"
									@select="choose('project', opt.value)"
								/>
							</div>
							<button v-if="form.project" type="button" class="btn-primary step__next" @click="nextStep">Continue</button>
						</div>

						<!-- STEP 2 -->
						<div v-else-if="step === 2" key="step-2" class="step">
							<button type="button" class="btn-back" @click="prevStep">← Back</button>
							<h4 ref="stepTitle" class="step__title" tabindex="-1">What's your budget range?</h4>
							<div class="options">
								<OptionCard
									v-for="opt in budgetOptions"
									:key="opt"
									:title="opt"
									:selected="form.budget === opt"
									@select="choose('budget', opt)"
								/>
							</div>
							<button v-if="form.budget" type="button" class="btn-primary step__next" @click="nextStep">Continue</button>
						</div>

						<!-- STEP 3 -->
						<div v-else-if="step === 3" key="step-3" class="step">
							<button type="button" class="btn-back" @click="prevStep">← Back</button>
							<h4 ref="stepTitle" class="step__title" tabindex="-1">Timeline and location</h4>

							<div class="field">
								<span id="timeline-label" class="field__label">When would you like to start? *</span>
								<div class="options options--compact" role="group" aria-labelledby="timeline-label">
									<OptionCard
										v-for="opt in timelineOptions"
										:key="opt"
										:title="opt"
										compact
										:selected="form.timeline === opt"
										@select="selectTimeline(opt)"
									/>
								</div>
								<div v-if="timelineError" class="field__error">{{ timelineError }}</div>
							</div>

							<div class="field">
								<label for="zip" class="field__label">Project ZIP code *</label>
								<input
									id="zip"
									ref="zip"
									type="text"
									inputmode="numeric"
									autocomplete="postal-code"
									maxlength="5"
									placeholder="28277"
									:value="form.zip"
									:class="{ 'is-invalid': zipError }"
									:aria-invalid="zipError ? 'true' : 'false'"
									@input="handleZipInput"
								>
								<div v-if="zipError" class="field__error">{{ zipError }}</div>
							</div>

							<button type="button" class="btn-primary step__next" @click="nextStep">Continue</button>
						</div>

						<!-- STEP 4 -->
						<div v-else key="step-4" class="step">
							<button type="button" class="btn-back" @click="prevStep">← Back</button>
							<h4 ref="stepTitle" class="step__title" tabindex="-1">How can we reach you?</h4>

							<div class="form__group">
								<div class="field">
									<label for="name" class="field__label">Full Name</label>
									<input id="name" v-model.trim="form.name" type="text" autocomplete="name" maxlength="50">
								</div>

								<div class="field">
									<label for="phone" class="field__label">Phone number *</label>
									<input
										id="phone"
										ref="phone"
										type="tel"
										inputmode="numeric"
										name="phone"
										autocomplete="tel"
										required
										v-model="formattedPhone"
										@input="handlePhoneInput"
										placeholder="+1 (555) 000-0000"
										:class="{ 'is-invalid': showError }"
										:aria-invalid="showError ? 'true' : 'false'"
									>
									<div v-if="showError" class="field__error">
										Please enter a valid 10-digit US phone number.
									</div>
								</div>

								<div class="field">
									<label for="email" class="field__label">Email</label>
									<input
										id="email"
										ref="email"
										v-model.trim="form.email"
										type="email"
										autocomplete="email"
										maxlength="100"
										:class="{ 'is-invalid': emailError }"
										:aria-invalid="emailError ? 'true' : 'false'"
										@input="emailError = ''"
									>
									<div v-if="emailError" class="field__error">{{ emailError }}</div>
								</div>

								<div class="field">
									<label for="job" class="field__label">Project details</label>
									<textarea
										id="job"
										v-model="form.job"
										rows="3"
										autocomplete="off"
										maxlength="300"
										placeholder="Anything else we should know — style, rooms, special requests…"
									></textarea>
								</div>
							</div>

							<div class="form__submit-block">
								<div class="form__submit-button">
									<button type="submit" class="btn-primary" :disabled="isSending">
										{{ isSending ? 'Sending…' : 'Request My Complimentary Estimate' }}
									</button>
									<div class="text text--note">We'll contact you within one business day.</div>
									<div class="text">By submitting this form you agree to our <router-link to="/privacy-policy">Privacy Policy.</router-link></div>
								</div>
							</div>
						</div>
					</transition>

					<div v-if="sendResult" class="form__send-result" role="alert">{{ sendResult }}</div>
				</form>
			</div>

		</div>
	</div>
</template>

<script>
import OptionCard from './OptionCard.vue';
import multiStepForm from '@/mixins/multiStepForm';

export default {
	name: 'FormView',

	components: { OptionCard },

	mixins: [multiStepForm],

	leadForm: {
		label: 'Main Form',
		attemptsKey: 'attempts_formview',
		formId: 'form-section',
		formName: 'main_contact_form',
		variant: 'main',
	},
};
</script>



<style lang="scss" scoped>
// ─── WRAPPER ─────────────────────────────────────────────
.page-wrapper {
	background-color: rgba(65, 77, 67, 1);
	padding: 40px 20px;
	box-sizing: border-box;

	@media (max-width: 790px) { padding: 0; }
}

.main {
	display: flex;
	max-width: 1200px;
	margin: 0 auto;
	//border-radius: 20px;
	overflow: hidden;

	@media (max-width: 790px) { flex-direction: column; }
}

// ─── LEFT ────────────────────────────────────────────────
.data {
	flex: 1;
	padding: 80px 50px 60px 60px;
	background-color: rgba(65, 77, 67, 1);
	display: flex;
	flex-direction: column;
	gap: 0;

	@media (max-width: 1080px) { padding: 60px 30px 50px 40px; }
	@media (max-width: 975px)  { padding: 40px 20px 30px 30px; }
	@media (max-width: 790px)  { padding: 30px 20px; }

	&__subtitle {
		font-size: 12px;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: rgba(255, 255, 255, 0.4);
		margin-bottom: 20px;
	}

	&__title {
		h2 {
			font-size: 34px;
			font-weight: 500;
			line-height: 1.2;
			color: #ffffff;
			margin: 0 0 20px;

			span { color: rgb(248, 171, 0); display: block; }
			.data__title--text { color: #ffffff; display: block; }

			@media (max-width: 975px) { font-size: 28px; }
			@media (max-width: 525px) { font-size: 22px; }
		}
	}

	&__text p {
		font-size: 14px;
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.5);
		margin: 0 0 24px;
	}

	ul {
		list-style: none;
		padding: 0 0 28px;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 10px;

		li {
			font-size: 13px;
			color: rgba(255, 255, 255, 0.5);
			position: relative;
			padding-left: 18px;

			&::before {
				content: '';
				position: absolute;
				left: 0;
				top: 50%;
				transform: translateY(-50%);
				width: 7px;
				height: 7px;
				background-color: #d89e1c;
				border-radius: 50%;
			}
		}
	}

	&__contacts {
		display: flex;
		flex-direction: column;
		gap: 14px;
		padding-top: 20px;
		border-top: 1px solid rgba(255, 255, 255, 0.1);
		margin-top: auto;
	}

	&__contact-item {
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	&__contact-label {
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 0.07em;
		color: rgba(255, 255, 255, 0.35);
		font-weight: 500;
	}

	a, &__contact-value {
		font-size: 14px;
		color: rgba(255, 255, 255, 0.85);
		text-decoration: none;
		transition: color 0.2s;
		&:hover { color: #ffffff; }
	}
}

// ─── RIGHT ───────────────────────────────────────────────
.forms {
	flex: 1;
	padding: 80px 80px 60px 50px;
	background-color: rgba(235, 240, 236, 0.15);
	border-left: 0.5px solid rgba(255, 255, 255, 0.1);
	min-width: 0;

	@media (max-width: 1080px) { padding: 60px 50px 50px 30px; }
	@media (max-width: 975px)  { padding: 40px 30px 30px 20px; }
	@media (max-width: 790px)  { padding: 30px 20px 50px; }
	@media (max-width: 400px)  { padding: 30px 16px 50px; }
}

.form {
	scroll-margin-top: 16px;

	h3 {
		font-size: 24px;
		font-weight: 500;
		line-height: 1.2;
		color: #ffffff;
		margin: 0 0 8px;
		@media (max-width: 790px) { font-size: 20px; }
	}

	&__lead {
		font-size: 13px;
		color: rgba(255, 255, 255, 0.45);
		margin: 0 0 24px;
		line-height: 1.5;
	}

	&__group {
		display: flex;
		flex-direction: column;
		row-gap: 24px;
	}

	&__submit-block { margin-top: 32px; }

	&__submit-button {
		.text {
			margin-top: 10px;
			font-size: 12px;
			color: rgba(255, 255, 255, 0.3);
			text-align: center;
			a { color: rgba(255, 255, 255, 0.3); text-decoration: none; }

			&--note {
				font-size: 13px;
				color: rgba(255, 255, 255, 0.55);
			}
		}
	}
}

.form__send-result {
	margin-top: 12px;
	font-size: 14px;
	text-align: center;
	color: #ff6b6b;
}

// ─── PROGRESS ────────────────────────────────────────────
.progress {
	list-style: none;
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 6px;
	padding: 0;
	margin: 0 0 28px;

	&__item {
		display: flex;
		flex-direction: column;
		gap: 8px;
		min-width: 0;
	}

	&__bar {
		height: 4px;
		border-radius: 100px;
		background-color: rgba(255, 255, 255, 0.15);
		transition: background-color 0.3s ease;
	}

	&__label {
		font-size: 12px;
		color: rgba(255, 255, 255, 0.4);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		transition: color 0.3s ease;

		@media (max-width: 400px) { font-size: 11px; }
	}

	&__item--active &__bar   { background-color: rgb(248, 171, 0); }
	&__item--active &__label { color: rgba(255, 255, 255, 0.85); }
}

// ─── STEP ────────────────────────────────────────────────
.step {
	display: flex;
	flex-direction: column;

	&__title {
		font-size: 18px;
		font-weight: 500;
		line-height: 1.3;
		color: #ffffff;
		margin: 0 0 18px;

		&:focus { outline: none; }
	}

	&__next { margin-top: 24px; }
}

.options {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;

	@media (max-width: 525px) { grid-template-columns: 1fr; }

	&--compact {
		gap: 10px;
		@media (max-width: 525px) { grid-template-columns: repeat(2, 1fr); }
	}
}

.field {
	display: flex;
	flex-direction: column;
	row-gap: 8px;

	& + & { margin-top: 24px; }

	&__label {
		font-size: 14px;
		font-weight: 400;
		color: rgba(255, 255, 255, 0.5);
	}

	&__error {
		color: #ff6b6b;
		font-size: 12px;
	}

	input,
	textarea {
		padding: 10px;
		font-size: 16px; // не меньше 16px — иначе iOS зумит страницу при фокусе
		font-family: inherit;
		border: none;
		border-bottom: 0.5px solid rgba(255, 255, 255, 0.25);
		border-radius: 0;
		background-color: transparent;
		color: #ffffff;

		&::placeholder { color: rgba(255, 255, 255, 0.2); }

		&:focus {
			outline: none;
			border-bottom: 1px solid rgba(255, 255, 255, 0.6);
		}

		&.is-invalid { border-bottom: 1px solid #ff6b6b; }
	}

	textarea {
		line-height: 1.5;
		resize: none;

		&::placeholder { font-style: italic; }
	}
}

.form__group .field + .field { margin-top: 0; }

// ─── BUTTONS ─────────────────────────────────────────────
.btn-primary {
	width: 100%;
	padding: 14px 40px;
	background-color: rgb(248, 171, 0);
	color: #ffffff;
	font-family: inherit;
	font-size: 15px;
	font-weight: 500;
	line-height: 1.1;
	border-radius: 100px;
	border: none;
	cursor: pointer;
	transition: opacity 0.2s ease;

	&:hover { opacity: 0.88; }
	&:focus-visible { outline: 2px solid #ffffff; outline-offset: 2px; }
	&:disabled { opacity: 0.6; cursor: default; }

	@media (max-width: 860px) { font-size: 14px; padding: 12px 28px; }
	@media (max-width: 400px) { padding: 12px 16px; }
}

.btn-back {
	align-self: flex-start;
	margin: -6px 0 10px -6px;
	padding: 6px;
	background: none;
	border: none;
	font-family: inherit;
	font-size: 13px;
	color: rgba(255, 255, 255, 0.5);
	cursor: pointer;
	transition: color 0.2s ease;

	&:hover { color: #ffffff; }
	&:focus-visible { outline: 1px solid rgba(255, 255, 255, 0.6); border-radius: 4px; }
}

// ─── STEP TRANSITION ─────────────────────────────────────
.step-fade-enter-active,
.step-fade-leave-active { transition: opacity 0.2s ease; }

.step-fade-enter-from,
.step-fade-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
	.step-fade-enter-active,
	.step-fade-leave-active { transition: none; }
}

// ─── PROMO ───────────────────────────────────────────────
// .promo-selection {
// 	padding: 14px;
// 	background: rgba(216, 158, 28, 0.12);
// 	border: 1px dashed rgba(216, 158, 28, 0.4);
// 	border-radius: 10px;
// 	pointer-events: none;
// 	user-select: none;

// 	.promo-label {
// 		display: flex;
// 		align-items: center;
// 		gap: 10px;

// 		input[type="radio"] {
// 			position: absolute;
// 			opacity: 0;
// 			width: 0; height: 0; margin: 0;
// 		}

// 		.radio-visual {
// 			width: 18px;
// 			height: 18px;
// 			border: 2px solid #d89e1c;
// 			border-radius: 50%;
// 			position: relative;
// 			flex-shrink: 0;

// 			&::after {
// 				content: '';
// 				position: absolute;
// 				top: 50%; left: 50%;
// 				transform: translate(-50%, -50%);
// 				width: 8px; height: 8px;
// 				background-color: #d89e1c;
// 				border-radius: 50%;
// 			}
// 		}

// 		.promo-text {
// 			font-size: 14px;
// 			color: #ffffff;
// 			font-weight: 500;
// 			line-height: 1.3;
// 		}
// 	}

// 	@media (max-width: 790px) {
// 		padding: 12px;
// 		.promo-label {
// 			gap: 10px;
// 			.radio-visual { width: 16px; height: 16px; &::after { width: 7px; height: 7px; } }
// 			.promo-text { font-size: 13px; }
// 		}
// 	}
// }
</style>